import { put } from 'redux-saga/effects';

import { type GeneratorResult, selectTyped, takeEvery } from '@pressreader/appstore';
import { loadIssueInfoByIssueKey, loadIssueRestrictionsByIssueKey } from '@pressreader/content';
import { ArticleFields } from '@pressreader/content-types';
import { extractCid, extractDateString } from '@pressreader/content-utils';
import { navigateToAction, selectNavConfig } from '@pressreader/navigation-core';
import { ViewName } from '@pressreader/navigation-types';
import { isPageViewNavConfig, navigateToArticleInCurrentPageViewAction, PageViewNavConfig, TextViewNavConfig } from '@pressreader/views-navigation';

import { getNavigateToArticleActionByIssueInfo, resolveIssueLanguageAssertedByNavConfig } from '../../utils';
import { navigateToArticleByIssueKey, navigateToPageAction, navigateToTextAction } from '../actions';
import { loadArticleById } from '../saga.utils';

export function* navigateToPage({ payload: { articleId, viewContextKey } }: ReturnType<typeof navigateToPageAction>): GeneratorResult<void> {
    const article = yield* loadArticleById(articleId, { requiredFields: ArticleFields.Issue | ArticleFields.Publication });

    if (!article?.issue) {
        return;
    }

    const { issue } = article;
    const { issueKey } = issue;

    const restrictions = yield* loadIssueRestrictionsByIssueKey(issueKey);

    if (restrictions?.replica) {
        yield put(
            navigateToAction({
                viewName: ViewName.IssueDetails,
                params: {
                    cid: extractCid(issueKey),
                    issueDate: extractDateString(issueKey),
                    country: issue.slugs.country,
                    newspaper: issue.slugs.newspaper,
                },
                replace: !!viewContextKey,
            }),
        );
        return;
    }

    const currentNavConfig = yield* selectTyped(selectNavConfig);
    // The in-place reveal targets the PageView config the navigation state still holds, which is not the
    // same as what is on screen: `navigateBegan` flips the view name while `navConfig` keeps the previous
    // view until `navigateDone`, so this can match while the article view is already mounted.
    if (isPageViewNavConfig(currentNavConfig) && currentNavConfig.issueKey === issueKey) {
        yield put(navigateToArticleInCurrentPageViewAction({ issueKey, articleId }));
        return;
    }

    const params: Partial<PageViewNavConfig> = {
        contextKey: viewContextKey,
        issueKey: issueKey,
        cid: extractCid(issueKey),
        issueDate: extractDateString(issueKey),
        country: issue.slugs.country,
        newspaper: issue.slugs.newspaper,
        page: issue.page.number.toString(),
        articleId,
        // The PageView opens in the language the view on screen asserts for this issue, the original otherwise.
        language: resolveIssueLanguageAssertedByNavConfig({ navConfig: currentNavConfig, issueKey }),
    };

    yield put(navigateToAction({ viewName: ViewName.PagesView, params, replace: !!viewContextKey }));
}

export function* navigateToText({ payload }: ReturnType<typeof navigateToTextAction>): GeneratorResult<void> {
    const articleId = payload.articleId;
    const article = yield* loadArticleById(articleId, { requiredFields: ArticleFields.Issue | ArticleFields.Publication });

    if (!article?.issue) {
        return;
    }

    const { issue } = article;
    const { issueKey } = issue;

    const currentNavConfig = yield* selectTyped(selectNavConfig);
    const params: Partial<TextViewNavConfig> = {
        contextKey: payload.viewContextKey,
        issueKey: issueKey,
        cid: extractCid(issueKey),
        issueDate: extractDateString(issueKey),
        country: issue.slugs.country,
        newspaper: issue.slugs.newspaper,
        page: issue.page.number.toString(),
        // The TextView opens in the language the view on screen asserts for this issue, the original otherwise.
        language: resolveIssueLanguageAssertedByNavConfig({ navConfig: currentNavConfig, issueKey }),
    };

    yield put(navigateToAction({ viewName: ViewName.TextView, params, replace: !!payload.viewContextKey }));
}

function* handleNavigateToArticleByIssueKey({ payload: { articleId, issueKey, displayMode } }: ReturnType<typeof navigateToArticleByIssueKey>) {
    const issueInfo = yield* loadIssueInfoByIssueKey(issueKey);

    if (!issueInfo) {
        return;
    }

    const action = getNavigateToArticleActionByIssueInfo(issueInfo, articleId);

    yield put({
        ...action,
        payload: {
            ...action.payload,
            displayMode,
        },
    });
}

export function* saga() {
    yield takeEvery(navigateToPageAction, navigateToPage);
    yield takeEvery(navigateToTextAction, navigateToText);
    yield takeEvery(navigateToArticleByIssueKey, handleNavigateToArticleByIssueKey);
}
